import { useCallback } from 'react';
import { SprintAPI } from '../../utils/api';

/**
 * Hook for fixing orphaned nested campaigns.
 * 
 * An orphaned campaign is one that has a parentSprintId but no corresponding
 * task node in the parent campaign. This can happen if:
 * - The parent campaign was deleted
 * - The task link was removed without updating the child
 * - Data corruption
 * 
 * This hook promotes orphaned campaigns back to top-level.
 */
export const useFixOrphanedCampaigns = (setSprints) => {
  /**
   * Fix orphaned nested campaigns by promoting them to top-level.
   * 
   * @param {Array} orphanedSprints - Array of sprint objects that are orphaned
   */
  const fixOrphanedCampaigns = useCallback(async (orphanedSprints) => {
    if (!orphanedSprints || orphanedSprints.length === 0) return;
    
    console.log(`[FIX] Fixing ${orphanedSprints.length} orphaned campaign(s)...`);
    
    for (const orphan of orphanedSprints) {
      try {
        // Fetch fresh data
        const freshOrphan = await SprintAPI.get(orphan.id);
        if (!freshOrphan) continue;
        
        // Remove parentSprintId to make it top-level
        const fixedSprint = {
          ...freshOrphan,
          parentSprintId: null,
        };
        
        // Save to API
        await SprintAPI.update(orphan.id, fixedSprint);
        
        // Update local state
        setSprints(prev => prev.map(s => 
          s.id === orphan.id ? fixedSprint : s
        ));
        
        console.log(`[FIX] Promoted orphaned campaign "${orphan.name}" to top-level`);
      } catch (error) {
        console.error(`[FIX] Failed to fix orphaned campaign "${orphan.name}":`, error);
      }
    }
  }, [setSprints]);

  return { fixOrphanedCampaigns };
};

export default useFixOrphanedCampaigns;
